import React, { useState } from 'react';
import Axios from 'axios';
import I18n from '@/modules/i18n';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import { useMutation } from '@tanstack/react-query';
import { SubmitButton } from '@/components/ui/Buttons';
import CommunicationSettings from './CommunicationSettings';
import ProgramAccessSettings from './ProgramAccessSettings';
import SecuritySettings from './SecuritySettings';

interface SubscriberSettings {
  only_provision_current_terms: boolean;
  receive_weekly_emails: boolean;
  session_timeout_in: number;
  strict_password_policies: string[];
  student_seats_locked: boolean;
  teacher_seats_locked: boolean;
}

interface AdminSettingsPanelProps {
  active: boolean;
  autoRostered: boolean;
  contactAdminsPath: string;
  isAdmin: boolean;
  onlyProvisionCurrentTerms: boolean;
  receiveWeeklyEmails: boolean;
  sessionTimeoutIn: number | null;
  sessionTimeoutInitialValue: string;
  sessionTimeoutOptions: Record<string, number>;
  studentSeatsLocked: boolean;
  subscriber: { id: number; strict_password_policies: string[] };
  subscriberApiPath: string;
  teacherSeatsLocked: boolean;
}

const DEFAULT_TIMEOUT = 60 * 24;

const settingsEqual = (a: SubscriberSettings, b: SubscriberSettings): boolean => {
  if (a.teacher_seats_locked !== b.teacher_seats_locked) return false;
  if (a.student_seats_locked !== b.student_seats_locked) return false;
  if (a.only_provision_current_terms !== b.only_provision_current_terms) return false;
  if (a.receive_weekly_emails !== b.receive_weekly_emails) return false;
  if (a.session_timeout_in !== b.session_timeout_in) return false;

  const aPolicies = [...a.strict_password_policies].sort();
  const bPolicies = [...b.strict_password_policies].sort();
  if (aPolicies.length !== bPolicies.length) return false;
  return aPolicies.every((v, i) => v === bPolicies[i]);
};

const AdminSettingsPanel = ({
  active,
  autoRostered,
  contactAdminsPath,
  isAdmin,
  onlyProvisionCurrentTerms,
  receiveWeeklyEmails,
  sessionTimeoutIn,
  sessionTimeoutInitialValue,
  sessionTimeoutOptions,
  studentSeatsLocked,
  subscriber,
  subscriberApiPath,
  teacherSeatsLocked,
}: AdminSettingsPanelProps) => {
  const buildInitial = (): SubscriberSettings => ({
    only_provision_current_terms: onlyProvisionCurrentTerms,
    receive_weekly_emails: receiveWeeklyEmails,
    session_timeout_in: sessionTimeoutIn ?? DEFAULT_TIMEOUT,
    strict_password_policies: subscriber.strict_password_policies,
    student_seats_locked: studentSeatsLocked,
    teacher_seats_locked: teacherSeatsLocked,
  });

  const [settings, setSettings] = useState<SubscriberSettings>(buildInitial);
  const [lastSaved, setLastSaved] = useState<SubscriberSettings>(buildInitial);

  const isPristine = settingsEqual(settings, lastSaved);

  const { isError, isLoading, mutate: saveSettings } = useMutation({
    mutationFn: (payload: SubscriberSettings) => Axios.patch(subscriberApiPath, {
      subscriber: payload,
    }),
    onError: () => setSettings(lastSaved),
    onSuccess: (_data, variables) => setLastSaved(variables),
  });

  const updateSetting = <K extends keyof SubscriberSettings>(
    key: K,
    value: SubscriberSettings[K],
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (!active) return null;

  const disabled = !isAdmin;

  return (
    <div>
      <ProgramAccessSettings
        autoRostered={autoRostered}
        disabled={disabled}
        settings={settings}
        subscriberId={subscriber.id}
        onToggle={(key, value) => updateSetting(key, value)}
      />

      <hr className="tw-my-6 tw-border-gray-200" />

      <SecuritySettings
        disabled={disabled}
        sessionTimeoutInitialValue={sessionTimeoutInitialValue}
        sessionTimeoutOptions={sessionTimeoutOptions}
        settings={settings}
        showSessionTimeout={isAdmin}
        onPoliciesChange={policies => updateSetting('strict_password_policies', policies)}
        onSessionTimeoutChange={value => updateSetting('session_timeout_in', value)}
      />

      <hr className="tw-my-6 tw-border-gray-200" />

      <CommunicationSettings
        contactAdminsPath={contactAdminsPath}
        disabled={disabled}
        receiveWeeklyEmails={settings.receive_weekly_emails}
        subscriberApiPath={subscriberApiPath}
        onToggle={checked => updateSetting('receive_weekly_emails', checked)}
      />

      <div className="tw-mt-6 tw-flex tw-items-center tw-justify-end tw-gap-2">
        {isError && (
          <span className="tw-text-red">
            {I18n.t('admin_settings_save_error')}
          </span>
        )}
        <SubmitButton
          disabled={isPristine}
          submitting={isLoading}
          onClick={() => saveSettings(settings)}
        >
          {I18n.t('update_button')}
        </SubmitButton>
      </div>
    </div>
  );
};

const AdminSettingsPanelWrapper = (props: AdminSettingsPanelProps) => (
  <ReactQueryProvider>
    <AdminSettingsPanel {...props} />
  </ReactQueryProvider>
);

export default AdminSettingsPanelWrapper;
